import { SITE_NAME, SITE_URL, absoluteUrl } from './seo';

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl('/icon.png'),
    sameAs: [
      'https://apps.apple.com/bg/app/tiny-spoon/id6754613061',
      'https://play.google.com/store/apps/details?id=com.f2digital.tinyspoon',
    ],
  };
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/foods?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function faqJsonLd(faqs = []) {
  if (!faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function articleJsonLd({
  title,
  description,
  path,
  image,
  datePublished,
  dateModified,
  authorName,
  inLanguage = 'en',
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    inLanguage,
    image: image ? [image] : undefined,
    mainEntityOfPage: absoluteUrl(path),
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: authorName || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/icon.png'),
      },
    },
  };
}

export function recipeJsonLd({
  title,
  description,
  path,
  image,
  images = [],
  datePublished,
  dateModified,
  authorName,
  ingredients = [],
  steps = [],
  prepTimeMinutes,
  minAgeMonths,
  inLanguage = 'en',
}) {
  const allImages = [image, ...images].filter(Boolean);
  return {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: title,
    description,
    inLanguage,
    image: allImages.length ? allImages : undefined,
    url: absoluteUrl(path),
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: authorName || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/icon.png'),
      },
    },
    recipeCategory: 'Baby-led weaning',
    recipeCuisine: 'Baby food',
    keywords: 'baby-led weaning, BLW, starting solids, how to serve, how to cut',
    recipeYield: '1 baby serving',
    recipeIngredient: ingredients,
    recipeInstructions: steps.map((text, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text,
    })),
    prepTime: prepTimeMinutes ? `PT${prepTimeMinutes}M` : undefined,
    cookTime: prepTimeMinutes ? `PT${prepTimeMinutes}M` : undefined,
    suitableForDiet: 'https://schema.org/LowSaltDiet',
    about: [
      { '@type': 'Thing', name: 'Baby-led weaning' },
      { '@type': 'Thing', name: 'Starting solids' },
      { '@type': 'Thing', name: 'How to serve baby food' },
    ],
    audience: minAgeMonths
      ? {
          '@type': 'PeopleAudience',
          suggestedMinAge: minAgeMonths,
        }
      : undefined,
  };
}

export function howToCutJsonLd({ title, path, cuts = [], inLanguage = 'en' }) {
  if (!cuts.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to cut foods for ${title}`,
    description: `Age-based baby-led weaning cut photos and descriptions for ${title}.`,
    inLanguage,
    url: `${absoluteUrl(path)}#how-to-cut`,
    step: cuts.map((cut, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: `How to cut ${cut.foodName} at ${cut.minAgeMonths} months`,
      text: cut.description,
      image: cut.imageUrl || undefined,
    })),
  };
}

export function howToServeJsonLd({ name, path, servings = [], inLanguage = 'en' }) {
  if (!servings.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to serve ${name} for baby-led weaning`,
    description: `Age-based photos and textures for serving ${name} to babies starting solids.`,
    inLanguage,
    url: `${absoluteUrl(path)}#how-to-serve`,
    step: servings.map((serving, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: `How to serve ${name} at ${serving.ageLabel}`,
      text: serving.description,
      image: serving.imageUrl || undefined,
    })),
  };
}

export function webPageJsonLd({ title, description, path, inLanguage = 'en' }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    inLanguage,
    url: absoluteUrl(path),
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.direct-answer', '.answer', 'h1', 'h2'],
    },
  };
}

export function itemListJsonLd(name, items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name || item.title,
      url: absoluteUrl(item.path),
    })),
  };
}
